import { useEffect, useState } from "react";
import { galaxyPatternVars, resolveFoilStyleId } from "./aiShine";
import { topographyPattern } from "./aiTopography";

const WARM_IMAGE_LIMIT = 96;
const FOIL_REVEAL_DELAY_MS = 80;
const FOIL_REVEAL_STEP_MS = 90;

const warmedImages = new Map();
const warmedPatterns = new Set();

export function scheduleFoilIdle(callback, timeout = 1500) {
  if (typeof window === "undefined") return () => {};
  if (typeof window.requestIdleCallback === "function") {
    const id = window.requestIdleCallback(callback, { timeout });
    return () => window.cancelIdleCallback(id);
  }
  const id = window.setTimeout(
    () => callback({ didTimeout: true, timeRemaining: () => 8 }),
    48,
  );
  return () => window.clearTimeout(id);
}

function warmImage(url) {
  if (!url || warmedImages.has(url)) return;
  const img = new Image();
  img.decoding = "async";
  img.src = url;
  warmedImages.set(url, img);
  if (warmedImages.size > WARM_IMAGE_LIMIT) {
    warmedImages.delete(warmedImages.keys().next().value);
  }
  if (typeof img.decode === "function") {
    img.decode().catch(() => {});
  }
}

function warmPattern(cardId, foilEffect) {
  const style = resolveFoilStyleId(foilEffect);
  if (!style) return;
  const key = `${style}:${cardId || ""}`;
  if (warmedPatterns.has(key)) return;
  warmedPatterns.add(key);
  if (style === "galaxy") galaxyPatternVars(cardId);
  if (style === "topography") topographyPattern(cardId);
}

export function prewarmFoilAssets(entries) {
  const queue = [];
  (entries || []).forEach((entry) => {
    if (!entry) return;
    (entry.imageUrls || []).forEach((url) => {
      if (url) queue.push(() => warmImage(url));
    });
    if (entry.foilEffect) {
      queue.push(() => warmPattern(entry.cardId, entry.foilEffect));
    }
  });
  if (!queue.length) return () => {};
  let cancelled = false;
  let cancelIdle = null;
  const run = (deadline) => {
    while (
      queue.length &&
      !cancelled &&
      (deadline.didTimeout || deadline.timeRemaining() > 4)
    ) {
      queue.shift()();
      if (deadline.didTimeout) break;
    }
    if (queue.length && !cancelled) cancelIdle = scheduleFoilIdle(run);
  };
  cancelIdle = scheduleFoilIdle(run);
  return () => {
    cancelled = true;
    cancelIdle?.();
  };
}

export function useDeferredFoilReveal(index = 0) {
  const [ready, setReady] = useState(false);
  useEffect(() => {
    if (ready) return undefined;
    let cancelIdle = null;
    const timer = window.setTimeout(
      () => {
        cancelIdle = scheduleFoilIdle(() => setReady(true), 400);
      },
      FOIL_REVEAL_DELAY_MS + Math.max(0, index) * FOIL_REVEAL_STEP_MS,
    );
    return () => {
      window.clearTimeout(timer);
      cancelIdle?.();
    };
  }, [index, ready]);
  return ready;
}
